import { z } from 'zod';

export const STOCK_SORTS = [
  'relevance',
  'price_asc',
  'price_desc',
  'year_desc',
  'year_asc',
  'mileage_asc',
  'mileage_desc',
  'newest',
  'distance',
] as const;

/** "12k" → 12000, "£12,500" → 12500; null, '', words and negatives → undefined. */
export const parseLooseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[£,\s]/g, '');
  const match = /^(\d+(?:\.\d+)?)(k)?$/.exec(cleaned);
  if (!match) return undefined;
  const n = Number(match[1]) * (match[2] ? 1000 : 1);
  return Number.isFinite(n) ? n : undefined;
};

const optionalNumber = z.preprocess(parseLooseNumber, z.number().nonnegative().optional());
const optionalText = (max: number) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() ? v.trim() : undefined),
    z.string().max(max).optional(),
  );

/** What a search is: shared by the URL, the filter bar, the AI and the search tool. */
export const stockSearchStateSchema = z.object({
  keywords: optionalText(300),
  make: optionalText(60),
  model: optionalText(60),
  body_type: optionalText(40),
  fuel_type: optionalText(40),
  transmission: optionalText(20),
  colour: optionalText(30),
  price_min: optionalNumber,
  price_max: optionalNumber,
  year_min: optionalNumber,
  year_max: optionalNumber,
  mileage_max: optionalNumber,
  postcode: optionalText(10),
  radius_miles: optionalNumber,
  sort: z.preprocess(
    (v) => (v === null || v === '' ? undefined : v),
    z.enum(STOCK_SORTS).optional(),
  ),
});
export type StockSearchState = z.infer<typeof stockSearchStateSchema>;

/** State fields that become search filters, in the order they are copied. */
const FILTER_FIELDS = [
  'make',
  'model',
  'body_type',
  'fuel_type',
  'transmission',
  'colour',
  'price_min',
  'price_max',
  'year_min',
  'year_max',
  'mileage_max',
] as const satisfies readonly (keyof StockSearchState)[];

/** Maps the state onto StockSearchService filter keys; undefined fields are left out. */
export const stockStateToFilters = (state: StockSearchState): Record<string, string | number> => {
  const filters: Record<string, string | number> = {};
  for (const field of FILTER_FIELDS) {
    const value = state[field];
    if (value !== undefined) filters[field === 'make' ? 'manufacturer' : field] = value;
  }
  return filters;
};

const sourcesSchema = z.array(z.string().min(1).max(64)).max(50).optional();

export const stockInterpretRequestSchema = z.object({
  message: z.string().trim().min(1).max(1000),
  state: stockSearchStateSchema,
  sources: sourcesSchema,
});
export type StockInterpretRequest = z.infer<typeof stockInterpretRequestSchema>;

export interface StockInterpretResponse {
  state: StockSearchState;
  summary: string;
  /** true when the AI reply was unusable and the words were searched instead. */
  fallback: boolean;
}

export const stockChatRequestSchema = z.object({
  messages: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().trim().min(1).max(4000),
      }),
    )
    .min(1)
    .max(20),
  state: stockSearchStateSchema,
  sources: sourcesSchema,
});
export type StockChatRequest = z.infer<typeof stockChatRequestSchema>;

export interface StockCarCard {
  id: string;
  title: string;
  price: number | null;
  year: number | null;
  mileage: number | null;
  fuel_type: string | null;
  transmission: string | null;
  image: string | null;
  distance_miles?: number;
}

export type StockAssistantEvent =
  | { type: 'status'; message: string }
  | { type: 'cars'; cars: StockCarCard[] }
  | { type: 'response'; content: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface StockTranscribeResponse {
  text: string;
}
